import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useResourcesPlanner } from "../ResourcesPlannerContext";
import { Avatar } from "@fluentui/react-components";
import { ChevronDown, ChevronRight, Folder, FolderOpen, FileText, Layers } from "lucide-react";
import styles from "../ResourcesPlanner.module.scss";
import {
  calculateAllocatedHoursInRange,
  calculateProjectAllocatedHoursInRange,
  calculateScopeAllocatedHoursInRange,
  calculateTaskAllocatedHoursInRange,
} from "../utils/dateUtils";
import moment from "moment";
import $ from "jquery";
import "daterangepicker";
import "daterangepicker/daterangepicker.css";
import HoverCard from "../../../components/hoverCard/HoverCard";
import CustomPopover from "../../../components/popover/Popover";
import CustomButton from "../../../components/controls/button/Button";
import { ArrowDownMedium, PersonPurpleIcon, CalendarIcon } from "../../../modules/default-svg";
import { TimelineTaskIcons } from "../../../helpers/utils";
import ResourceAllocationPopover from "./ResourceAllocationPopover";
import { getDecryptedCookie, setEncryptedCookie } from "../../../utils/cookieUtils";

const LEFT_PANEL_MIN_WIDTH = 240;
const LEFT_PANEL_MAX_WIDTH = 600;
const LEFT_PANEL_DEFAULT_WIDTH = 310;
const LEFT_PANEL_COOKIE_KEY = "resourcePlannerLeftPanelWidth";

const PERIOD_OPTIONS = [
  { value: "last_week", label: "Last week" },
  { value: "this_week", label: "This week" },
  { value: "next_week", label: "Next week" },
  { value: "last_month", label: "Last month" },
  { value: "this_month", label: "This month" },
  { value: "next_month", label: "Next month" },
  { value: "custom", label: "Custom" },
];

const ALLOCATION_BAR_HEIGHT = 20;
const ALLOCATION_BAR_GAP = 5;
const ROW_MIN_HEIGHT = 40;
const RESOURCE_ROW_MIN_HEIGHT = 52;

function countWorkingDays(startDate, endDate) {
  let count = 0;
  const rs = new Date(startDate);
  const re = new Date(endDate);
  const cur = new Date(rs);
  while (cur <= re) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

function countAllocLanes(allocations) {
  if (!allocations || allocations.length === 0) return 0;
  const sorted = [...allocations].sort((a, b) =>
    a.allocation.startDate.localeCompare(b.allocation.startDate)
  );
  const lanes = [];
  for (const alloc of sorted) {
    const allocStart = moment(alloc.allocation.startDate);
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      if (allocStart.isAfter(moment(lanes[i]), "day")) {
        lanes[i] = alloc.allocation.endDate;
        placed = true;
        break;
      }
    }
    if (!placed) lanes.push(alloc.allocation.endDate);
  }
  return lanes.length;
}

function collectAllocations(projects) {
  const allocs = [];
  for (const project of projects || []) {
    for (const scope of project.scopes || []) {
      for (const task of scope.tasks || []) {
        if (task.allocation?.startDate && task.allocation?.endDate)
          allocs.push({ task, scope, project, allocation: task.allocation });
      }
    }
  }
  return allocs;
}

function collectProjectAllocations(project) {
  const allocs = [];
  for (const scope of project.scopes || []) {
    for (const task of scope.tasks || []) {
      if (task.allocation?.startDate && task.allocation?.endDate)
        allocs.push({ task, scope, project, allocation: task.allocation });
    }
  }
  return allocs;
}

function collectScopeAllocations(scope, project) {
  const allocs = [];
  for (const task of scope.tasks || []) {
    if (task.allocation?.startDate && task.allocation?.endDate)
      allocs.push({ task, scope, project, allocation: task.allocation });
  }
  return allocs;
}

function getRowHeight(allocations, isResource) {
  const laneCount = countAllocLanes(allocations);
  const minH = isResource ? RESOURCE_ROW_MIN_HEIGHT : ROW_MIN_HEIGHT;
  const contentHeight = laneCount > 0
    ? laneCount * (ALLOCATION_BAR_HEIGHT + ALLOCATION_BAR_GAP) + 8
    : 0;
  return Math.max(contentHeight, minH);
}

function computeResourceAssignment(resource) {
  const resourceWeekly = resource.maxWeeklyHours ?? 40;
  const totalAssignmentWeekly = (resource.projects || []).reduce(
    (sum, p) => sum + (p.maxWeeklyHours ?? 0), 0
  );
  return {
    resourceWeekly,
    totalAssignmentWeekly,
    isOverAssigned: totalAssignmentWeekly > resourceWeekly,
    overrun: Math.max(0, parseFloat((totalAssignmentWeekly - resourceWeekly).toFixed(2))),
  };
}

export default function PlannerLeftPanel({ onScrollToAllocation }) {
  const { resources, isExpanded, toggleExpand, summaryPeriod, selectedRange, handlePeriodChange, setCustomDateRange, isLoadingMore, hasMore, allocationDisplayMode, setAllocationDisplayMode } = useResourcesPlanner();
  const [openPeriodPopover, setIsOpenPeriodPopover] = useState(false);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customStartInput, setCustomStartInput] = useState("");
  const [customEndInput, setCustomEndInput] = useState("");

  // ── Resizable panel ──────────────────────────────────────────────
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = getDecryptedCookie(LEFT_PANEL_COOKIE_KEY);
    const parsed = saved ? Number(saved) : NaN;
    return Number.isFinite(parsed) && parsed >= LEFT_PANEL_MIN_WIDTH && parsed <= LEFT_PANEL_MAX_WIDTH
      ? parsed
      : LEFT_PANEL_DEFAULT_WIDTH;
  });
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(panelWidth);
  const panelRef = useRef(null);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isResizing.current) return;
      const delta = e.clientX - startX.current;
      const newWidth = Math.min(LEFT_PANEL_MAX_WIDTH, Math.max(LEFT_PANEL_MIN_WIDTH, startWidth.current + delta));
      // Mutate the DOM directly to avoid re-renders during drag
      if (panelRef.current) {
        panelRef.current.style.width = `${newWidth}px`;
        panelRef.current.style.minWidth = `${newWidth}px`;
        panelRef.current.style.maxWidth = `${newWidth}px`;
      }
    };
    const onMouseUp = () => {
      if (!isResizing.current) return;
      isResizing.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      // Read final width from DOM element and commit to state + cookie
      if (panelRef.current) {
        const finalWidth = parseInt(panelRef.current.style.width, 10);
        setPanelWidth(finalWidth);
        setEncryptedCookie(LEFT_PANEL_COOKIE_KEY, String(finalWidth));
      }
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = panelRef.current
      ? parseInt(panelRef.current.style.width, 10) || panelWidth
      : panelWidth;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [panelWidth]);

  const customStartRef = useRef(null);
  const customEndRef = useRef(null);
  const customStartValRef = useRef("");
  const customEndValRef = useRef("");
  customStartValRef.current = customStartInput;
  customEndValRef.current = customEndInput;

  const settings = JSON.parse(localStorage.getItem("settings"));
  const dateFormat =
    settings?.regionalSettings?.dateFormat
      ?.replace(/dd/g, "DD")
      ?.replace(/yy/g, "YY")
      ?.replace(/mm/g, "MM") || "MM/DD/YYYY";

  useEffect(() => {
    if (!showCustomPicker) return;
    const timeout = setTimeout(() => {
      if (customStartRef.current) {
        $(customStartRef.current).daterangepicker(
          {
            singleDatePicker: true,
            startDate: customStartValRef.current ? moment(customStartValRef.current, "YYYY-MM-DD") : moment(),
            locale: { format: dateFormat },
            autoApply: true,
          },
          (chosen) => {
            const dateStr = chosen.format("YYYY-MM-DD");
            setCustomStartInput(dateStr);
            if (customEndValRef.current && dateStr > customEndValRef.current) {
              setCustomEndInput(dateStr);
              if (customEndRef.current) {
                try { $(customEndRef.current).data("daterangepicker")?.setStartDate(moment(dateStr)); } catch (e) { /* ignore */ }
              }
            }
          }
        );
      }
      if (customEndRef.current) {
        $(customEndRef.current).daterangepicker(
          {
            singleDatePicker: true,
            startDate: customEndValRef.current ? moment(customEndValRef.current, "YYYY-MM-DD") : moment(),
            locale: { format: dateFormat },
            autoApply: true,
          },
          (chosen) => {
            const dateStr = chosen.format("YYYY-MM-DD");
            setCustomEndInput(dateStr);
            if (customStartValRef.current && dateStr < customStartValRef.current) {
              setCustomStartInput(dateStr);
              if (customStartRef.current) {
                try { $(customStartRef.current).data("daterangepicker")?.setStartDate(moment(dateStr)); } catch (e) { /* ignore */ }
              }
            }
          }
        );
      }
    }, 50);
    return () => {
      clearTimeout(timeout);
      if (customStartRef.current) {
        try { $(customStartRef.current).data("daterangepicker")?.remove(); } catch (e) { /* ignore */ }
      }
      if (customEndRef.current) {
        try { $(customEndRef.current).data("daterangepicker")?.remove(); } catch (e) { /* ignore */ }
      }
    };
  }, [showCustomPicker, dateFormat]);

  const periodRange = selectedRange;

  const periodCapacity = useCallback(
    (maxWeeklyHours) => {
      const hoursPerDay = (maxWeeklyHours || 40) / 5;
      const days = countWorkingDays(periodRange.startDate, periodRange.endDate);
      return parseFloat((days * hoursPerDay).toFixed(2));
    },
    [periodRange]
  );

  const handleTaskClick = useCallback(
    (task) => {
      if (task.allocation?.startDate) onScrollToAllocation(task.allocation.startDate);
    },
    [onScrollToAllocation]
  );

  const handleApplyCustom = useCallback(() => {
    if (customStartInput && customEndInput && customStartInput <= customEndInput) {
      setCustomDateRange(customStartInput, customEndInput);
      setIsOpenPeriodPopover(null);
      setShowCustomPicker(false);
    }
  }, [customStartInput, customEndInput, setCustomDateRange]);

  const getResourceSummary = useCallback(
    (resource) => calculateAllocatedHoursInRange(resource.projects, periodRange.startDate, periodRange.endDate, resource.maxWeeklyHours || 40),
    [periodRange]
  );

  const getProjectAllocated = useCallback(
    (project) => calculateProjectAllocatedHoursInRange(project, periodRange.startDate, periodRange.endDate),
    [periodRange]
  );

  const getScopeAllocated = useCallback(
    (scope) => calculateScopeAllocatedHoursInRange(scope, periodRange.startDate, periodRange.endDate),
    [periodRange]
  );

  const getTaskAllocated = useCallback(
    (task) => calculateTaskAllocatedHoursInRange(task, periodRange.startDate, periodRange.endDate),
    [periodRange]
  );

  const rows = useMemo(() => {
    const result = [];

    for (const resource of resources) {
      const resourceExp = isExpanded(resource.id);
      const allAllocs = collectAllocations(resource.projects);
      const summary = getResourceSummary(resource);
      const assignment = computeResourceAssignment(resource);
      const assignmentCap = periodCapacity(assignment.totalAssignmentWeekly);

      if (!resourceExp) {
        result.push({
          type: "resource", resource, rowHeight: getRowHeight(allAllocs, true),
          summary, assignment, assignmentCap, expanded: false,
        });
      } else {
        result.push({
          type: "resource", resource, rowHeight: RESOURCE_ROW_MIN_HEIGHT,
          summary, assignment, assignmentCap, expanded: true,
        });

        for (const project of resource.projects || []) {
          const projectExp = isExpanded(resource.id, project.id);
          const projAllocs = collectProjectAllocations(project);
          const projAllocated = getProjectAllocated(project);
          const projCap = periodCapacity(project.maxWeeklyHours || 0);
          const isProjectOverAssigned = (project.maxWeeklyHours || 0) > (resource.maxWeeklyHours ?? 40);

          if (!projectExp) {
            result.push({
              type: "project", resource, project,
              rowHeight: getRowHeight(projAllocs, false),
              projAllocated, projCap, isProjectOverAssigned, expanded: false,
            });
          } else {
            result.push({
              type: "project", resource, project,
              rowHeight: ROW_MIN_HEIGHT,
              projAllocated, projCap, isProjectOverAssigned, expanded: true,
            });

            for (const scope of project.scopes || []) {
              const scopeExp = isExpanded(resource.id, project.id, scope.id);
              const scopeAllocs = collectScopeAllocations(scope, project);
              const scopeAllocated = getScopeAllocated(scope);
              const hasTaskOver = (scope.tasks || []).some((t) => {
                const taskAlloc = getTaskAllocated(t);
                return projCap > 0 && taskAlloc > projCap;
              });

              if (!scopeExp) {
                result.push({
                  type: "scope", resource, project, scope,
                  rowHeight: getRowHeight(scopeAllocs, false),
                  scopeAllocated, hasTaskOver, projCap, expanded: false,
                });
              } else {
                result.push({
                  type: "scope", resource, project, scope,
                  rowHeight: ROW_MIN_HEIGHT,
                  scopeAllocated, hasTaskOver, projCap, expanded: true,
                });

                const sortedTasks = [...(scope.tasks || [])].sort((a, b) => {
                  const aD = a.allocation?.startDate || "9999";
                  const bD = b.allocation?.startDate || "9999";
                  return aD.localeCompare(bD);
                });

                for (const task of sortedTasks) {
                  const taskAllocs = (task.allocation?.startDate && task.allocation?.endDate)
                    ? [{ task, scope, project, allocation: task.allocation }] : [];
                  const taskAllocated = getTaskAllocated(task);

                  result.push({
                    type: "task", resource, project, scope, task,
                    rowHeight: getRowHeight(taskAllocs, false),
                    taskAllocated, projCap,
                  });
                }
              }
            }
          }
        }
      }
    }

    return result;
  }, [resources, isExpanded, periodCapacity, getResourceSummary, getProjectAllocated, getScopeAllocated, getTaskAllocated]);

  return (
    <div ref={panelRef} className={styles.leftPanel} style={{ width: panelWidth, minWidth: panelWidth, maxWidth: panelWidth }}>
      <div className={styles.leftPanelResizeHandle} onMouseDown={handleResizeStart} />
      <div className={styles.leftPanelHeader}>
        <PersonPurpleIcon />
        <div className={styles.displayToggle}>
          <button
            type="button"
            className={`${styles.toggleBtn} ${allocationDisplayMode === "hours" ? styles.toggleBtnActive : ""}`}
            onClick={() => setAllocationDisplayMode("hours")}
          >
            hrs
          </button>
          <button
            type="button"
            className={`${styles.toggleBtn} ${allocationDisplayMode === "percentage" ? styles.toggleBtnActive : ""}`}
            onClick={() => setAllocationDisplayMode("percentage")}
          >
            %
          </button>
        </div>
        <div className={styles.periodDropdown}>
          <CustomPopover
            icon={
              <CustomButton
                label={summaryPeriod === "custom" && selectedRange
                  ? `${moment(selectedRange.startDate).format("MMM D")} - ${moment(selectedRange.endDate).format("MMM D")}`
                  : PERIOD_OPTIONS.find(option => option.value === summaryPeriod)?.label}
                btnClass={`${styles.periodDropdownOption} d_btn `}
                rightIcon={
                  <span className="ms-6 d-inline-flex">
                    <ArrowDownMedium />
                  </span>
                }
                clickHandler={() => {
                  setIsOpenPeriodPopover(!openPeriodPopover);
                  if (!openPeriodPopover) setShowCustomPicker(false);
                }}
              />
            }
            width={showCustomPicker ? "240px" : "140px"}
            PopSurfaceClass={styles.periodDropdownPopover}
            status={openPeriodPopover}
          >
            {PERIOD_OPTIONS.map((option) => (
              <div
                key={option.value}
                className={styles.periodDropdownPopoverItem}
                onClick={() => {
                  if (option.value === "custom") {
                    setCustomStartInput(selectedRange?.startDate || "");
                    setCustomEndInput(selectedRange?.endDate || "");
                    setShowCustomPicker(true);
                  } else {
                    handlePeriodChange(option.value);
                    setIsOpenPeriodPopover(null);
                    setShowCustomPicker(false);
                  }
                }}
              >
                {option.label}
              </div>
            ))}
            {showCustomPicker && (
              <div className={styles.customDatePicker}>
                <div className={styles.customDateInputRow}>
                  <label className={styles.customDateLabel}>From</label>
                  <div className={styles.customDateInputWrap}>
                    <CalendarIcon />
                    <input
                      ref={customStartRef}
                      type="text"
                      readOnly
                      className={styles.customDateInput}
                    />
                  </div>
                </div>
                <div className={styles.customDateInputRow}>
                  <label className={styles.customDateLabel}>To</label>
                  <div className={styles.customDateInputWrap}>
                    <CalendarIcon />
                    <input
                      ref={customEndRef}
                      type="text"
                      readOnly
                      className={styles.customDateInput}
                    />
                  </div>
                </div>
                <div className={styles.customDateActions}>
                  <button
                    type="button"
                    className={styles.customCancelBtn}
                    onClick={() => setShowCustomPicker(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={styles.customApplyBtn}
                    onClick={handleApplyCustom}
                    disabled={!customStartInput || !customEndInput || customStartInput > customEndInput}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </CustomPopover>
        </div>
      </div>
      <div className={styles.leftPanelBody}>
        {rows.map((row, idx) => {
          if (row.type === "resource") {
            const { assignment, summary, assignmentCap } = row;
            const hasProjects = row?.resource?.projects?.length > 0;
            const allocated = summary.allocated;
            const isOver = assignment.isOverAssigned || allocated > summary.capacity;
            const utilizationPct = assignmentCap > 0
              ? Math.round((allocated / assignmentCap) * 100)
              : 0;
            const summaryData = { ...assignment, allocated, assignmentCap, resourceCap: summary.capacity };

            return (
              <div
                key={`lr-${row.resource.id}`}
                className={`${styles.leftPanelRow} ${styles.leftPanelRowResource} ${isOver ? styles.leftPanelRowResourceOver : ""}`}
                style={{ height: row.rowHeight }}
                onClick={() => toggleExpand(row.resource.id)}
              >
                <span className={`${styles.expandIcon} ${!hasProjects ? "invisible" : ""}`}>
                  {row.expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>

                <HoverCard
                  HoverCardTrigger={
                    <div>
                      <Avatar
                        size={28}
                        color="colorful"
                        name={row.resource.name}
                        image={{ src: row.resource.image || null }}
                      />
                    </div>
                  }
                  AvatarId={row.resource.id}
                  AvatarName={row.resource.name}
                  AvatarImage={row.resource.image}
                  AvatarDesignation={row.resource.title}
                  AvatarEmail={row.resource.email}
                  AvatarPhone={row.resource.phoneNumber}
                  AvatarIsExternal={row.resource.isExternal}
                  CanSendMessage={true}
                />
                <div className={styles.resourceInfo}>
                  <div className={styles.resourceName}>
                    <span className={styles.resourceNameText}>{row.resource.name}</span>
                    {row.resource.title && <span className={styles.resourceTitleText}>{row.resource.title}</span>}
                  </div>
                  <div className={styles.resourceAllocationPill}>
                    <ResourceAllocationPopover
                      level="resource"
                      resource={row.resource}
                      data={summaryData}
                    >
                      <span className={`${styles.allocationPill} ${isOver ? styles.pillOver : styles.pillNormal}`}>
                        {allocationDisplayMode === "hours"
                          ? `${allocated} / ${assignmentCap} hrs`
                          : `${utilizationPct}%`
                        }
                      </span>
                    </ResourceAllocationPopover>
                  </div>
                </div>
              </div>
            );
          }

          if (row.type === "project") {

            const hasScopes = row.project?.scopes?.length > 0;
            return (
              <div
                key={`lp-${row.resource.id}-${row.project.id}`}
                className={`${styles.leftPanelRow} ${styles.leftPanelRowProject}`}
                style={{ height: row.rowHeight }}
                onClick={() => hasScopes && toggleExpand(row.resource.id, row.project.id)}
              >
                <span className={`${styles.expandIcon} ${!hasScopes ? "invisible" : ""}`}>
                  {row.expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </span>
                <div className="flex-shrink-0 w-3 h-3 flex items-center justify-center mr-1">
                  {TimelineTaskIcons[row.project.status]()}
                </div>
                <div className={styles.resourceInfo}>
                  <div className={styles.resourceName}>
                    <span className={styles.resourceNameText}>{row.project.name}</span>
                  </div>
                  <div className={styles.resourceAllocationPill}>
                    <ResourceAllocationPopover
                      level="project"
                      resource={row.resource}
                      project={row.project}
                      data={{
                        projAllocated: row.projAllocated,
                        projCap: row.projCap,
                        isProjectOverAssigned: row.isProjectOverAssigned,
                        projectMaxWeekly: row.project.maxWeeklyHours || 0,
                        resourceMaxWeekly: row.resource.maxWeeklyHours ?? 40,
                        scopes: (row.project.scopes || []).map((s) => ({
                          id: s.id,
                          name: s.name,
                          allocated: getScopeAllocated(s),
                        })),
                      }}
                    >
                      <span className={`${styles.allocationPill} ${(row.isProjectOverAssigned || row.projAllocated > row.projCap) ? styles.pillOver : styles.pillNormal}`}>
                        {allocationDisplayMode === "hours"
                          ? `${row.projAllocated}h / ${row.projCap}h`
                          : `${row.projCap > 0 ? Math.round((row.projAllocated / row.projCap) * 100) : 0}%`
                        }
                      </span>
                    </ResourceAllocationPopover>
                  </div>
                </div>
              </div>
            );
          }

          if (row.type === "scope") {
            const hasTasks = row.scope?.tasks?.length > 0;
            return (
              <div
                key={`ls-${row.resource.id}-${row.project.id}-${row.scope.id}`}
                className={`${styles.leftPanelRow} ${styles.leftPanelRowScope}`}
                style={{ height: row.rowHeight }}
                onClick={() => hasTasks && toggleExpand(row.resource.id, row.project.id, row.scope.id)}
              >
                <span className={`${styles.expandIcon} ${!hasTasks ? "invisible" : ""}`}>
                  {row.expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                </span>
                <div className="flex-shrink-0 w-3 h-3 flex items-center justify-center mr-1">
                  {TimelineTaskIcons[row.scope.status]()}
                </div>
                <div className={styles.resourceInfo}>
                  <div className={styles.resourceName}>
                    <span className={styles.resourceNameText}>{row.scope.name}</span>
                  </div>
                  <div className={styles.resourceAllocationPill}>
                    <ResourceAllocationPopover
                      level="scope"
                      resource={row.resource}
                      project={row.project}
                      scope={row.scope}
                      data={{
                        scopeAllocated: row.scopeAllocated,
                        projectMaxWeekly: row.project.maxWeeklyHours || 0,
                        projCap: row.projCap,
                        tasks: (row.scope.tasks || []).map((t) => ({
                          id: t.id,
                          name: t.name,
                          status: t.status,
                          allocation: t.allocation,
                          allocated: getTaskAllocated(t),
                        })),
                      }}
                    >
                      <span className={`${styles.allocationPill} ${row.hasTaskOver ? styles.pillOver : styles.pillNormal}`}>
                        {row.scopeAllocated}h
                      </span>
                    </ResourceAllocationPopover>
                  </div>
                </div>
              </div>
            );
          }

          if (row.type === "task") {
            return (
              <div
                key={`lt-${row.resource.id}-${row.project.id}-${row.scope.id}-${row.task.id}`}
                className={`${styles.leftPanelRow} ${styles.leftPanelRowTask} ${row.task.allocation?.startDate ? styles.taskRowClickable : styles.taskRowNoDate
                  }`}
                style={{ height: row.rowHeight }}
                onClick={() => handleTaskClick(row.task)}
              >
                <div className="flex-shrink-0 w-3 h-3 flex items-center justify-center mr-1">
                  {TimelineTaskIcons[row.task.status]()}
                </div>
                <div className={styles.resourceInfo}>
                  <div className={styles.resourceName}>
                    <span className={styles.resourceNameText}>{row.task.name}</span>
                  </div>
                  <div className={styles.resourceAllocationPill}>
                    {row.task.allocation?.startDate ? (
                      <ResourceAllocationPopover
                        level="task"
                        resource={row.resource}
                        project={row.project}
                        scope={row.scope}
                        task={row.task}
                        data={{
                          taskAllocated: row.taskAllocated,
                          projectMaxWeekly: row.project.maxWeeklyHours || 0,
                          projCap: row.projCap,
                        }}
                      >
                        <span className={`${styles.allocationPill} ${(row.projCap > 0 && row.taskAllocated > row.projCap)
                          ? styles.pillOver : styles.pillNormal
                          }`}>
                          {row.taskAllocated}h
                        </span>
                      </ResourceAllocationPopover>
                    ) : (
                      <span className={styles.noDateBadge}>No dates</span>
                    )}
                  </div>
                </div>
              </div>
            );
          }

          return null;
        })}

        {isLoadingMore && (
          <>
            <div className={styles.skeletonRow}>
              <div className={styles.skeletonAvatar} />
              <div className={styles.skeletonLines}>
                <div className={`${styles.skeletonLine} ${styles.skeletonLineName}`} />
                <div className={`${styles.skeletonLine} ${styles.skeletonLinePill}`} />
              </div>
            </div>
            <div className={styles.skeletonRow}>
              <div className={styles.skeletonAvatar} />
              <div className={styles.skeletonLines}>
                <div className={`${styles.skeletonLine} ${styles.skeletonLineName}`} />
                <div className={`${styles.skeletonLine} ${styles.skeletonLinePill}`} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
